import { Switch } from '@headlessui/react'
import { Button } from '@heroui/button'
import RequestData from 'components/Dashboard/Parameters/RequestData'
import Voice from 'components/Dashboard/Parameters/Voice'
import PhoneInput from 'components/Hero/PhoneInput'
import { useAuth } from 'hooks/useAuth'
import { Pause, Play } from 'lucide-react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { $fetch } from 'utils/fetch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import FlowContext from '../contextFlow'

export default function WarmTransferOptions({
  warmTransferFields,
  setWarmTransferFields,
  currentNodeId,
}) {
  const [isWarmTransferDisabled, setIsWarmTransferDisabled] = useState(false)
  const [isTestWarmTransferModalOpen, setIsTestWarmTransferModalOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [holdMusicSelection, setHoldMusicSelection] = useState('default')
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false)
  const [audioElement, setAudioElement] = useState(null)
  const { org } = useAuth()
  const { elements, triggerUpdate } = useContext(FlowContext)

  const holdMusicOptions = [
    {
      key: 'melodic-flute',
      label: 'Melodic Flute Serenade',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/Mellotroniac_-_Flight_Of_Young_Hearts_Flute.mp3',
    },
    {
      key: 'chopin-nocturne',
      label: 'Chopin Nocturne',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/ith_chopin-15-2.mp3',
    },
    {
      key: 'string-ensemble',
      label: 'String Ensemble Interlude',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/BusyStrings.mp3',
    },
    {
      key: 'orchestral-overture',
      label: 'Orchestral Overture',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/MARKOVICHAMP-Borghestral.mp3',
    },
    {
      key: 'brahms-intermezzo',
      label: 'Brahms Intermezzo',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/ith_brahms-116-4.mp3',
    },
    {
      key: 'farewell-melody',
      label: 'Farewell Melody',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/oldDog_-_endless_goodbye_%28instr.%29.mp3',
    },
    {
      key: 'clockwork-waltz',
      label: 'Clockwork Waltz',
      url: 'http://com.twilio.music.classical.s3.amazonaws.com/ClockworkWaltz.mp3',
    },
    {
      key: 'custom',
      label: 'Custom Hold Music',
      url: '',
    },
  ]

  const methods = useForm({
    defaultValues: {
      phone_number: '',
      encrypted_key: '',
      request_data: '',
      voice: '',
    },
  })
  const updateField = useCallback(
    (field, value) => {
      setWarmTransferFields((prev) => {
        const updated = { ...prev, [field]: value }

        // Handle edge creation/removal for voicemail target nodes asynchronously
        if ((field === 'voicemailTargetNode' || field === 'voicemailResponseType' || field === 'useVoicemailMessage') && currentNodeId) {
          const currentEdges = elements.edges
          const existingVoicemailEdges = currentEdges.filter(edge =>
            edge.data?.isVoicemailEdge && edge.source === currentNodeId,
          )

          const shouldHaveEdge = updated.useVoicemailMessage && updated.voicemailResponseType === 'pathway' && updated.voicemailTargetNode?.id
          const targetNodeId = updated.voicemailTargetNode?.id

          if (shouldHaveEdge) {
            const edgeId = `${currentNodeId}-${targetNodeId}`
            const edgeExists = currentEdges.some(edge => edge.id === edgeId)

            if (!edgeExists) {
              const newEdge = {
                id: edgeId,
                source: currentNodeId,
                target: targetNodeId,
                type: 'custom',
                animated: true,
                data: {
                  label: 'Voicemail',
                  isVoicemailEdge: true,
                },
              }

              const updatedEdges = existingVoicemailEdges.length > 0
                ? currentEdges.filter(edge => !(edge.data?.isVoicemailEdge && edge.source === currentNodeId)).concat(newEdge)
                : [...currentEdges, newEdge]

              triggerUpdate({
                nodes: elements.nodes, // Just pass the existing nodes reference
                edges: updatedEdges,
              })
            }
          }
          else if (existingVoicemailEdges.length > 0) {
            const updatedEdges = currentEdges.filter(edge =>
              !(edge.data?.isVoicemailEdge && edge.source === currentNodeId),
            )

            triggerUpdate({
              nodes: elements.nodes, // Just pass the existing nodes reference
              edges: updatedEdges,
            })
          }
        }

        return updated
      })
    },
    [setWarmTransferFields, elements, currentNodeId, triggerUpdate],
  )

  const handleSendProxyCall = async () => {
    setIsSending(true)

    try {
      const values = methods.getValues()
      const { voicemailTargetNode, ...rest } = warmTransferFields
      await $fetch('/v1/warm_transfer/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',

          'encrypted_key': values.encrypted_key,
        },
        body: JSON.stringify({
          phone_number: values.phone_number,
          voice: values.voice,
          request_data: values.request_data ? Object.fromEntries(values.request_data.map(item => [item.key, item.value])) : {},
          warm_transfer_fields: {
            ...rest,
            ...(voicemailTargetNode === null || voicemailTargetNode === undefined
              ? {}
              : { voicemailTargetNode }
            ),
          },
        }),
      })
    }
    catch (error) {
      console.error(error)
    }
    finally {
      setIsSending(false)
    }
  }

  useEffect(() => {
    const fetchUserData = async () => {
      if (org?.org_type !== 'enterprise') {
        setIsWarmTransferDisabled(true)
        updateField('isEnabled', false)
      }
      else {
        if (warmTransferFields.optimizeForIVR === undefined) {
          updateField('optimizeForIVR', true)
        }
        if (warmTransferFields.useVoicemailMessage === undefined) {
          updateField('useVoicemailMessage', true)
          updateField(
            'voicemailMessage',
            'Hey, the agent we tried to dial is not available at the moment. Please try calling us back again later. I\'ll be ending the call now, thank you!',
          )
        }

        if (warmTransferFields.voicemailResponseType === undefined) {
          if (warmTransferFields.useVoicemailMessage === true) {
            updateField('voicemailResponseType', 'message')
          }
          else {
            updateField('voicemailResponseType', 'message')
          }
        }

        if (warmTransferFields.voicemailTargetNode === undefined || warmTransferFields.voicemailTargetNode === null) {
          updateField('voicemailTargetNode', null)
        }
        else {
          updateField('voicemailTargetNode', warmTransferFields.voicemailTargetNode)
        }

        if (warmTransferFields.useTimeout === undefined) {
          updateField('useTimeout', false)
        }
        if (warmTransferFields.timeout === undefined) {
          updateField('timeout', 0)
        }
      }
    }

    fetchUserData()
  }, [updateField])

  // Initialize hold music selection based on current URL
  useEffect(() => {
    if (warmTransferFields.holdMusicUrl) {
      const matchingOption = holdMusicOptions.find(option => option.url === warmTransferFields.holdMusicUrl)
      if (matchingOption) {
        setHoldMusicSelection(matchingOption.key)
      }
      else {
        setHoldMusicSelection('custom')
      }
    }
    else {
      setHoldMusicSelection('default')
    }
  }, [warmTransferFields.holdMusicUrl])

  const handlePreviewToggle = useCallback(() => {
    const selectedOption = holdMusicOptions.find(option => option.key === holdMusicSelection)
    const audioUrl = selectedOption?.url || warmTransferFields.holdMusicUrl

    if (!audioUrl)
      return

    if (isPreviewPlaying) {
      if (audioElement) {
        audioElement.pause()
        audioElement.currentTime = 0
      }
      setIsPreviewPlaying(false)
    }
    else {
      // Start playing
      if (audioElement) {
        audioElement.pause()
      }

      const audio = new Audio(audioUrl)
      audio.addEventListener('ended', () => {
        setIsPreviewPlaying(false)
        setAudioElement(null)
      })
      audio.addEventListener('error', () => {
        setIsPreviewPlaying(false)
        setAudioElement(null)
      })

      audio.play().then(() => {
        setIsPreviewPlaying(true)
        setAudioElement(audio)
      }).catch(() => {
        setIsPreviewPlaying(false)
        setAudioElement(null)
      })
    }
  }, [holdMusicSelection, warmTransferFields.holdMusicUrl, isPreviewPlaying, audioElement, holdMusicOptions])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause()
        setAudioElement(null)
      }
    }
  }, [audioElement])

  return (
    <div className="rounded border p-2.5 shadow-xs">
      <div className="flex items-center justify-between">
        <h3 className="text-base">Warm Transfer</h3>
        <Switch
          checked={warmTransferFields.isEnabled}
          onChange={value => updateField('isEnabled', value)}
          disabled={isWarmTransferDisabled}
          className={`
            ${
    warmTransferFields.isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
    }
            relative inline-flex h-4 w-7 items-center rounded-full
            ${
    isWarmTransferDisabled ? 'cursor-not-allowed opacity-50' : ''
    }
          `}
        >
          <span className="sr-only">Enable Warm Transfer</span>
          <span
            className={`
              ${
    warmTransferFields.isEnabled ? 'translate-x-4' : 'translate-x-0.5'
    }
              inline-block h-2.5 w-2.5 transform rounded-full bg-white
              transition
            `}
          />
        </Switch>
      </div>
      <p className="mt-1.5 text-xs text-gray-600">
        Enable warm transfer options to configure how the call is transferred to
        a live agent.
      </p>
      {isWarmTransferDisabled && (
        <p className="mt-1.5 text-xs text-red-500">
          Warm transfers are for enterprise accounts only.
        </p>
      )}
      {warmTransferFields.isEnabled && !isWarmTransferDisabled && (
        <p className="mt-1.5 text-xs text-gray-600">
          When a customer requests a transfer, a proxy agent (a second AI agent)
          calls the transfer number. The live agent can talk to the customer,
          ask questions about the call, and then merge the calls. The agent
          merges the calls and introduces the two parties to each other.
        </p>
      )}
      {warmTransferFields.isEnabled && !isWarmTransferDisabled && (

        <div className="mt-4 flex flex-col gap-y-4">

          <div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="use-custom-proxy-number"
                className="text-xs font-medium"
              >
                Proxy Agent Number
              </Label>
              <Switch
                checked={warmTransferFields.useCustomFromNumber}
                onChange={(value) => {
                  updateField('useCustomFromNumber', value)
                  if (!value) {
                    updateField('fromNumber', '')
                  }
                }}
                className={`
                  ${
        warmTransferFields.useCustomFromNumber
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Use Custom Proxy Number</span>
                <span
                  className={`
                    ${
        warmTransferFields.useCustomFromNumber
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              The phone number that the proxy agent will call from. A default
              number is used if not enabled.
            </p>
            {warmTransferFields.useCustomFromNumber && (
              <Input
                id="proxy-number"
                value={warmTransferFields.fromNumber}
                onChange={e => updateField('fromNumber', e.target.value)}
                placeholder="Enter the proxy agent's phone number"
                className="mt-1.5"
              />
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="agent-prompt" className="text-xs font-medium">
                Live Agent Briefing Prompt
              </Label>
              <div className="flex items-center gap-x-1.5">
                <Switch
                  checked={warmTransferFields.isAgentPromptStatic}
                  onChange={value =>
                    updateField('isAgentPromptStatic', value)}
                  className={`
                    ${
        warmTransferFields.isAgentPromptStatic
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                    relative inline-flex h-4 w-7 items-center rounded-full
                  `}
                >
                  <span className="sr-only">Use Static Text</span>
                  <span
                    className={`
                      ${
        warmTransferFields.isAgentPromptStatic
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                      inline-block h-2.5 w-2.5 transform rounded-full bg-white
                      transition
                    `}
                  />
                </Switch>
                <Label htmlFor="agent-prompt-static" className="text-xs">
                  Static Text
                </Label>
              </div>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              This prompt is used to brief the live agent about the call before
              merging.
            </p>
            <textarea
              id="agent-prompt"
              value={warmTransferFields.agentPrompt}
              onChange={e => updateField('agentPrompt', e.target.value)}
              placeholder={
                warmTransferFields.isAgentPromptStatic
                  ? 'Enter the exact text to brief the live agent'
                  : 'Enter the prompt to brief the live agent'
              }
              className={`
                mt-0.5 min-h-[80px] w-full rounded border border-gray-300 p-1.5
              `}
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="allow-agent-conversation"
                className="text-xs font-medium"
              >
                Allow Live Agent Conversation
              </Label>
              <Switch
                checked={warmTransferFields.allowMergeControl}
                onChange={value => updateField('allowMergeControl', value)}
                className={`
                  ${
        warmTransferFields.allowMergeControl
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Allow Live Agent Conversation</span>
                <span
                  className={`
                    ${
        warmTransferFields.allowMergeControl
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              When enabled, allows the proxy agent to have a conversation with the live agent before merging the call. If enabled, make sure to specify merge behavior by specifying when to use the "merge" tool.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="use-hold-music" className="text-xs font-medium">
                Hold Music
              </Label>
              <Switch
                checked={warmTransferFields.useCustomHoldMusic}
                onChange={value => updateField('useCustomHoldMusic', value)}
                className={`
                  ${
        warmTransferFields.useCustomHoldMusic
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Use Hold Music</span>
                <span
                  className={`
                    ${
        warmTransferFields.useCustomHoldMusic
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              When enabled, the user will hear hold music while the proxy agent
              briefs the live agent and waits for them to be ready.
            </p>
            {warmTransferFields.useCustomHoldMusic && (
              <div className="mt-1.5 flex flex-col gap-y-2">
                <div className="flex flex-col gap-y-1">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className={`
                        mb-1 block text-xs font-medium text-gray-700
                      `}
                      >
                        Hold Music Options
                      </label>
                      <Select
                        value={holdMusicSelection}
                        onValueChange={(value) => {
                          setHoldMusicSelection(value)
                          const selectedOption = holdMusicOptions.find(option => option.key === value)
                          if (selectedOption && selectedOption.key !== 'custom') {
                            updateField('holdMusicUrl', selectedOption.url)
                          }
                          else if (value === 'custom') {
                            updateField('holdMusicUrl', '')
                          }
                          if (audioElement) {
                            audioElement.pause()
                            audioElement.currentTime = 0
                            setIsPreviewPlaying(false)
                            setAudioElement(null)
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue placeholder="Select hold music" />
                        </SelectTrigger>
                        <SelectContent className="z-10000">
                          {holdMusicOptions.map(option => (
                            <SelectItem key={option.key} value={option.key}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(() => {
                      const selectedOption = holdMusicOptions.find(option => option.key === holdMusicSelection)
                      const hasValidUrl = (selectedOption?.url || (holdMusicSelection === 'custom' && warmTransferFields.holdMusicUrl))

                      return hasValidUrl && (
                        <button
                          type="button"
                          onClick={handlePreviewToggle}
                          className={`
                            flex h-8 w-8 items-center justify-center rounded-md
                            border border-gray-300 transition-colors
                            hover:bg-slate-50
                          `}
                        >
                          {isPreviewPlaying
                            ? (
                                <Pause size={14} />
                              )
                            : (
                                <Play size={14} />
                              )}
                        </button>
                      )
                    })()}
                  </div>
                </div>
                {holdMusicSelection === 'custom' && (
                  <Input
                    id="hold-music-url"
                    value={warmTransferFields.holdMusicUrl}
                    onChange={e => updateField('holdMusicUrl', e.target.value)}
                    placeholder="Enter custom URL for hold music"
                    className="w-full"
                  />
                )}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="merge-call-prompt"
                className="text-xs font-medium"
              >
                Merge Call Prompt
              </Label>
              <div className="flex items-center gap-x-1.5">
                <Switch
                  checked={warmTransferFields.isMergeCallPromptStatic}
                  onChange={value =>
                    updateField('isMergeCallPromptStatic', value)}
                  className={`
                    ${
        warmTransferFields.isMergeCallPromptStatic
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                    relative inline-flex h-4 w-7 items-center rounded-full
                  `}
                >
                  <span className="sr-only">Use Static Text</span>
                  <span
                    className={`
                      ${
        warmTransferFields.isMergeCallPromptStatic
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                      inline-block h-2.5 w-2.5 transform rounded-full bg-white
                      transition
                    `}
                  />
                </Switch>
                <Label htmlFor="merge-call-prompt-static" className="text-xs">
                  Static Text
                </Label>
              </div>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              This prompt generates the message said to both the user and the
              live agent when merging the call. If set as static text, the exact
              sentence will be used.
            </p>
            <textarea
              id="merge-call-prompt"
              value={warmTransferFields.mergeCallPrompt}
              onChange={e => updateField('mergeCallPrompt', e.target.value)}
              placeholder={
                warmTransferFields.isMergeCallPromptStatic
                  ? 'Enter the exact text to be said when merging the call'
                  : 'Enter the prompt to be said when merging the call'
              }
              className={`
                mt-0.5 min-h-[80px] w-full rounded border border-gray-300 p-1.5
              `}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="optimize-ivr" className="text-xs font-medium">
                Optimize for IVR/Queue Systems
              </Label>
              <Switch
                checked={warmTransferFields.optimizeForIVR}
                onChange={value => updateField('optimizeForIVR', value)}
                className={`
                  ${
        warmTransferFields.optimizeForIVR
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Optimize for IVR/Queue Systems</span>
                <span
                  className={`
                    ${
        warmTransferFields.optimizeForIVR
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Enables wait_for_greeting, ivr_mode, and
              blocks interruptions when transferring to IVR systems or if the
              agent has to wait in a queue.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="timeout"
                className="text-xs font-medium"
              >
                Hold Timeout
              </Label>
              <Switch
                checked={warmTransferFields.useTimeout}
                onChange={value => updateField('useTimeout', value)}
                className={`
                  ${
        warmTransferFields.useTimeout
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Enable Hold Timeout</span>
                <span
                  className={`
                    ${
        warmTransferFields.useTimeout
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Maximum time (in seconds) the proxy agent will wait on hold before hanging up.
            </p>
            {warmTransferFields.useTimeout && (
              <Input
                id="timeout"
                value={warmTransferFields.timeout}
                onChange={e => updateField('timeout', e.target.value)}
                className="mt-1.5"
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="voicemail-handling"
                className="text-xs font-medium"
              >
                Leave Voicemail Message
              </Label>
              <Switch
                checked={warmTransferFields.useVoicemailMessage}
                onChange={(value) => {
                  updateField('useVoicemailMessage', value)
                }}
                className={`
                  ${
        warmTransferFields.useVoicemailMessage
          ? 'bg-indigo-600'
          : 'bg-gray-200'
        }
                  relative inline-flex h-4 w-7 items-center rounded-full
                `}
              >
                <span className="sr-only">Enable Leave Voicemail Message</span>
                <span
                  className={`
                    ${
        warmTransferFields.useVoicemailMessage
          ? 'translate-x-4'
          : 'translate-x-0.5'
        }
                    inline-block h-2.5 w-2.5 transform rounded-full bg-white
                    transition
                  `}
                />
              </Switch>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Configure what happens when the transfer to the agent fails.
              {!warmTransferFields.useVoicemailMessage && ' Using default message.'}
            </p>

            {warmTransferFields.useVoicemailMessage && (
              <div className="mt-2 flex flex-col gap-y-3">
                <div>
                  <Label
                    htmlFor="voicemail-response-type"
                    className="text-xs font-medium"
                  >
                    Response Type
                  </Label>
                  <select
                    value={warmTransferFields.voicemailResponseType || 'message'}
                    onChange={(e) => {
                      const newHandling = e.target.value
                      updateField('voicemailResponseType', newHandling)

                      // Remove existing voicemail edges when switching handling type
                      if (newHandling === 'message') {
                        updateField('voicemailTargetNode', null)
                      }
                      else {
                        updateField('voicemailMessage', '')
                      }
                    }}
                    className={`
                      mt-1 block w-full rounded-md border border-gray-300 p-1.5
                      text-sm text-gray-900
                      focus:border-indigo-500 focus:ring-indigo-500
                    `}
                  >
                    <option value="message">End Call Message</option>
                    <option value="pathway">Go to Node</option>
                  </select>
                </div>

                {warmTransferFields.voicemailResponseType === 'message' && (
                  <div>
                    <Label
                      htmlFor="voicemail-message"
                      className="text-xs font-medium"
                    >
                      End Call Message
                    </Label>
                    <textarea
                      id="voicemail-message"
                      value={warmTransferFields.voicemailMessage}
                      onChange={e => updateField('voicemailMessage', e.target.value)}
                      placeholder="Hey, the agent we tried to dial is not available at the moment. Please try calling us back again later. I'll be ending the call now, thank you!"
                      className={`
                        mt-1 min-h-[80px] w-full rounded border border-gray-300
                        p-1.5
                      `}
                    />
                  </div>
                )}

                {warmTransferFields.voicemailResponseType === 'pathway' && (
                  <div>
                    <Label
                      htmlFor="voicemail-target-node"
                      className="text-xs font-medium"
                    >
                      Target Node
                    </Label>
                    <select
                      id="voicemail-target-node"
                      value={JSON.stringify(warmTransferFields.voicemailTargetNode || { id: '', name: '' })}
                      onChange={(e) => {
                        const value = JSON.parse(e.target.value)
                        const selectedNode = elements.nodes.find(node => node.id === value.id)
                        updateField('voicemailTargetNode', {
                          id: value.id,
                          name: selectedNode?.data.name || '',
                        })
                      }}
                      className={`
                        mt-1 block w-full rounded-md border border-gray-300
                        p-1.5 text-sm text-gray-900
                        focus:border-indigo-500 focus:ring-indigo-500
                      `}
                    >
                      <option value={JSON.stringify({ id: '', name: '' })}>Select Node</option>
                      {elements.nodes
                        .filter(node => node.data && node.data.name)
                        .slice()
                        .sort((a, b) => a.data.name.localeCompare(b.data.name))
                        .map(node => (
                          <option
                            key={node.id}
                            value={JSON.stringify({
                              id: node.id,
                              name: node.data.name,
                            })}
                          >
                            {node.data.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="mt-2.5 text-xs text-gray-500">
            Note: You can reference variables from any call in these prompts.
          </p>

          <div className="mt-4">
            <Button
              size="sm"
              className={`
                flex w-full items-center justify-center gap-2 border
                border-gray-300 bg-white text-gray-700 shadow-xs
                hover:bg-gray-50
              `}
              onClick={(e) => {
                e.stopPropagation()
                setIsTestWarmTransferModalOpen(prev => !prev)
              }}
            >
              <span>Test Proxy Agent</span>
              <svg
                className={`
                  h-4 w-4 transition-transform
                  ${isTestWarmTransferModalOpen
          ? `rotate-180`
          : ''}
                `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>

            </Button>
            {isTestWarmTransferModalOpen && (
              <div className="mt-2 flex flex-col gap-y-2">
                <FormProvider {...methods}>
                  <div className="mt-2 flex flex-col items-center gap-y-4">
                    <div>
                      <PhoneInput
                        value={methods.watch('phone_number')}
                        onChange={value => methods.setValue('phone_number', value)}
                        label="Phone Number"
                        description="Enter the phone number to send the proxy agent to"
                      />
                    </div>
                    <Voice fieldName="voice" />
                    <RequestData fieldName="request_data" />
                  </div>

                  <div className="flex w-full items-center justify-between">
                    <Button
                      size="sm"
                      className={`
                        w-full bg-indigo-600 text-white
                        hover:bg-indigo-700
                      `}
                      onClick={handleSendProxyCall}
                      disabled={isSending}
                    >
                      {isSending ? 'Sending...' : 'Send Call From Proxy Agent'}
                    </Button>
                  </div>
                </FormProvider>
              </div>
            )}
          </div>
        </div>

      )}
    </div>
  )
}
